import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import { useT } from '../i18n';
import { BACKEND_URL as RUNTIME_BACKEND_URL } from '../runtimeConfig';

/*
  Read-only campaign dashboard for sharing with advertisers / clients
  (audit Phase 2 §6.2.A). Resolved by token via:
    GET /api/public/dashboard/{token}
  No auth header is sent. The backend gates which metrics are returned
  per-link, so the renderer just iterates whatever the server gives back.
*/

const BACKEND_URL = RUNTIME_BACKEND_URL;

const METRIC_LABELS = {
  total_clicks: 'Total clicks',
  unique_clicks: 'Unique clicks',
  conversions: 'Conversions',
  conversion_rate: 'Conversion rate',
  revenue_total: 'Revenue',
  cost_total: 'Cost',
  epc: 'EPC',
  roi: 'ROI',
};

function formatMetric(key, value) {
  if (value === null || value === undefined) {
    return '—';
  }
  if (key === 'conversion_rate' || key === 'roi') {
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return `${num.toFixed(2)}%`;
  }
  if (key === 'revenue_total' || key === 'cost_total' || key === 'epc') {
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    return num.toFixed(2);
  }
  if (typeof value === 'number') {
    return value.toLocaleString();
  }
  if (Array.isArray(value)) {
    return null; // tabular metrics rendered separately
  }
  return String(value);
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function BreakdownTable({ title, rows, keyField }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="mb-2 text-sm font-medium">{title}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="py-1">{keyField}</th>
            <th className="py-1 text-right">Clicks</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 10).map((row, i) => (
            <tr key={i} className="border-b border-border/50 last:border-0">
              <td className="py-1">{row._id || '—'}</td>
              <td className="py-1 text-right tabular-nums">{(row.count || 0).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PublicDashboard() {
  const { token } = useParams();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/public/dashboard/${encodeURIComponent(token)}`)
      .then((response) => {
        if (cancelled) return;
        setData(response.data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 410) {
          setError(t('This shared link has expired or been revoked.'));
        } else if (status === 404) {
          setError(t('This shared link is not valid.'));
        } else if (status === 429) {
          setError(t('Too many requests — please retry in a moment.'));
        } else {
          setError(t('Could not load the shared dashboard.'));
        }
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('Shared dashboard')}
          </div>
          <h1 className="mt-1 text-2xl font-semibold">
            {data?.label || data?.campaign?.name || t('Campaign dashboard')}
          </h1>
          {data?.campaign?.description ? (
            <p className="mt-1 text-sm text-muted-foreground">{data.campaign.description}</p>
          ) : null}
          {data?.expires_at ? (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('Expires')}: {new Date(data.expires_at).toLocaleString()}
            </p>
          ) : null}
        </div>

        {loading ? (
          <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
            {t('Loading…')}
          </div>
        ) : null}

        {error ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {!loading && !error && data ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {Object.entries(data.metrics || {}).map(([key, value]) => {
                const formatted = formatMetric(key, value);
                if (formatted === null) return null; // arrays handled below
                return (
                  <MetricCard
                    key={key}
                    label={t(METRIC_LABELS[key] || key)}
                    value={formatted}
                  />
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <BreakdownTable
                title={t('By country')}
                rows={data.metrics?.clicks_by_country}
                keyField={t('Country')}
              />
              <BreakdownTable
                title={t('By device')}
                rows={data.metrics?.clicks_by_device}
                keyField={t('Device')}
              />
              <BreakdownTable
                title={t('By browser')}
                rows={data.metrics?.clicks_by_browser}
                keyField={t('Browser')}
              />
            </div>

            <div className="text-xs text-muted-foreground">
              {t('Read-only public view — generated by xpnsr-tracker.')}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
